import type { BoundingRect } from '@/src/components/features/search/Map/types';

export type MapViewportSize = {
  width: number;
  height: number;
};

export type MapPositionFromViewport = {
  center: { lat: number; lng: number };
  zoom: number;
  bounds: BoundingRect;
};

export const DEFAULT_MAP_VIEWPORT_SIZE: MapViewportSize = {
  width: 800,
  height: 600,
} as const;

type QueryValue = string | Array<string> | undefined;
type QueryShape = Record<string, QueryValue>;

const TILE_SIZE = 256;
const MAX_MERCATOR_LAT = 85.05112878;
const MAX_ZOOM = 21;
const MIN_ZOOM = 0;
const DEFAULT_EPSILON = 0.000001;
const LISTING_BOUNDS_DECIMALS = 5;

function firstValue(value: QueryValue): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function roundTo(value: number, decimals: number): number {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}

function lngToWorldX(lng: number, worldSize: number): number {
  return ((lng + 180) / 360) * worldSize;
}

function latToWorldY(lat: number, worldSize: number): number {
  const clampedLat = clamp(lat, -MAX_MERCATOR_LAT, MAX_MERCATOR_LAT);
  const sin = Math.sin((clampedLat * Math.PI) / 180);
  return (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * worldSize;
}

function worldXToLng(x: number, worldSize: number): number {
  return (x / worldSize) * 360 - 180;
}

function worldYToLat(y: number, worldSize: number): number {
  const n = Math.PI - (2 * Math.PI * y) / worldSize;
  return (Math.atan(Math.sinh(n)) * 180) / Math.PI;
}

export function calculateBoundsFromCenterZoom(
  center: { lat: number; lng: number },
  zoom: number,
  viewport: MapViewportSize,
): BoundingRect {
  const normalizedZoom = clamp(zoom, MIN_ZOOM, MAX_ZOOM);
  const worldSize = TILE_SIZE * Math.pow(2, normalizedZoom);
  const centerX = lngToWorldX(center.lng, worldSize);
  const centerY = latToWorldY(center.lat, worldSize);
  const halfWidth = viewport.width / 2;
  const halfHeight = viewport.height / 2;

  return {
    northLat: clamp(worldYToLat(centerY - halfHeight, worldSize), -MAX_MERCATOR_LAT, MAX_MERCATOR_LAT),
    southLat: clamp(worldYToLat(centerY + halfHeight, worldSize), -MAX_MERCATOR_LAT, MAX_MERCATOR_LAT),
    eastLng: clamp(worldXToLng(centerX + halfWidth, worldSize), -180, 180),
    westLng: clamp(worldXToLng(centerX - halfWidth, worldSize), -180, 180),
  };
}

export function mapPositionFromCenterZoom(
  center: { lat: number; lng: number },
  zoom: number,
  viewport: MapViewportSize,
): MapPositionFromViewport {
  const normalizedCenter = {
    lat: clamp(center.lat, -MAX_MERCATOR_LAT, MAX_MERCATOR_LAT),
    lng: clamp(center.lng, -180, 180),
  };
  const normalizedZoom = clamp(zoom, MIN_ZOOM, MAX_ZOOM);

  return {
    center: normalizedCenter,
    zoom: normalizedZoom,
    bounds: calculateBoundsFromCenterZoom(normalizedCenter, normalizedZoom, viewport),
  };
}

export function parseMapPositionFromQuery(
  query: QueryShape,
  viewport: MapViewportSize,
): MapPositionFromViewport | null {
  const lat = Number.parseFloat(firstValue(query.lat) ?? '');
  const lng = Number.parseFloat(firstValue(query.lon) ?? '');
  const zoom = Number.parseFloat(firstValue(query.z) ?? '');

  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(zoom)) {
    return null;
  }

  if (lat < -MAX_MERCATOR_LAT || lat > MAX_MERCATOR_LAT || lng < -180 || lng > 180) {
    return null;
  }

  if (zoom < MIN_ZOOM || zoom > MAX_ZOOM) {
    return null;
  }

  return mapPositionFromCenterZoom({ lat, lng }, zoom, viewport);
}

export function parseMapPositionFromSearchParams(
  params: URLSearchParams,
  viewport: MapViewportSize,
): MapPositionFromViewport | null {
  return parseMapPositionFromQuery(
    {
      lat: params.get('lat') ?? undefined,
      lon: params.get('lon') ?? undefined,
      z: params.get('z') ?? undefined,
    },
    viewport,
  );
}

export function boundsMatchListingParams(
  bounds: BoundingRect,
  params: {
    minLat?: number;
    maxLat?: number;
    minLon?: number;
    maxLon?: number;
  },
  epsilon = DEFAULT_EPSILON,
): boolean {
  if (params.minLat == null || params.maxLat == null || params.minLon == null || params.maxLon == null) {
    return false;
  }

  const normalizedBounds = normalizeBoundsForListingQuery(bounds);
  const normalizedParams = normalizeBoundsForListingQuery({
    southLat: params.minLat,
    northLat: params.maxLat,
    westLng: params.minLon,
    eastLng: params.maxLon,
  });

  return (
    Math.abs(normalizedBounds.southLat - normalizedParams.southLat) <= epsilon &&
    Math.abs(normalizedBounds.northLat - normalizedParams.northLat) <= epsilon &&
    Math.abs(normalizedBounds.westLng - normalizedParams.westLng) <= epsilon &&
    Math.abs(normalizedBounds.eastLng - normalizedParams.eastLng) <= epsilon
  );
}

export function normalizeBoundsForListingQuery(bounds: BoundingRect): BoundingRect {
  return {
    northLat: roundTo(bounds.northLat, LISTING_BOUNDS_DECIMALS),
    southLat: roundTo(bounds.southLat, LISTING_BOUNDS_DECIMALS),
    eastLng: roundTo(bounds.eastLng, LISTING_BOUNDS_DECIMALS),
    westLng: roundTo(bounds.westLng, LISTING_BOUNDS_DECIMALS),
  };
}
